import { Mass } from "@/helpers/Mass";
import { Money } from "@/helpers/Money";
import { check } from "@/helpers/utils";
import { CartAddLineItem } from "@/services/shopifyCart";
import {
  getEffectiveSubscriptionDiscountPercent,
  getEffectiveOnetimeSpotDiscountDollars,
  parseVariantSpotDiscountDollars,
} from "@/helpers/constants";
import {
  getSubscriptionPlanNameForVariation,
  getSubscriptionDiscountsByVariationFromPlans,
  getSubscriptionDiscountVariation,
  SUBSCRIPTION_CONTROL_PLAN_NAME,
  SUBSCRIPTION_PLAN_NAME_BY_VARIATION,
  SUBSCRIPTION_15_PLAN_DISCOUNT_PERCENT,
} from "@/helpers/subscriptionDiscount";

type DroppsBaseVariant = {
  /** product variant is considered available for sale on shopify. */
  available: boolean;
  backorderEnabled: boolean;
  compareAtPrice?: Money;
  hidden: boolean;
  id: number;
  inventoryQuantity?: number;
  price: Money;
  requiresShipping: boolean;
  size: {
    value: string;
    unit?: string;
  };
  package_quantity: number;
  default_variant_multiplier: number;
  tiered_discount_variant_multiplier?: number | null;
  quantity_discount?: number;
  sku?: string;
  taxable?: boolean;
  override_title?: string;
  /** Title of variant (eg: "Standard / Starter Kit") */
  title: string;
  weight: Mass;
  /** Fixed USD off per unit from variant metafield `custom.product_spot_discount` (one-time discount test) */
  productSpotDiscount?: number;

  /**
   * See {@link variantSelect.orderableSubscription} to know if this
   * subscription can be ordered
   */
  subscription?: DroppsSubscription;
  metafields?: any;
};

export type DroppsVariant = {
  // kind: "standard";
} & DroppsBaseVariant;

export type DroppsDiscountFormula =
  | {
      kind: "percentage";
      percentage: number;
    }
  | {
      kind: "fixed";
      offset: Money;
    }
  | {
      kind: "price";
      price: Money;
    };

export type DroppsDiscount =
  | {
      kind: "percentage";
      percentage: number;
      price: Money;
    }
  | {
      kind: "fixed";
      offset: Money;
      price: Money;
    }
  | {
      kind: "price";
      price: Money;
    };

export type DroppsSubscription =
  | {
      kind: "variant";
      variantId: number;
      discount: DroppsDiscount;
      hidden: boolean;
    }
  | {
      kind: "selling-plan";
      discount: DroppsDiscount;
      hidden: boolean;
    };

export type DroppsProduct = {
  /** Small amount of text or image that can be badged onto the product image */
  badge?: Badge;

  collections?: Array<{
    id: string;
    handle: string;
    title: string;
  }>;

  /** Front Box Image */
  frontBoxImage?: string;

  /** Items that are also included with a product */
  bundledItems?: DroppsProduct[];

  /** A user quote about the product */
  featuredQuote?: string;

  /** A text pseudo-id like "stain-odor-laundry-detergent-pods-ocean" */
  handle: string;

  /** If a product should not be displayed or be orderable */
  hidden: boolean;

  /** If the subscriptions should be hidden on the front-end, but still processed by recharge*/
  hideSubscription: boolean;

  /** section to replace the visuals for future shipments on PDP pages */
  overrideFutureShipments?: string;

  id: number;

  /** List of videos and images for a product */
  media: ShopifyMedia[];

  /** List of Shopify */
  tags: string[];

  /**
   * The full name of a product, like "Ultra Concentrated Laundry Detergent
   * Starter Kit Stain & Odor, Lavender Eucalyptus"
   *
   * {@link productSelect.scent}
   * {@link productSelect.name}
   */
  title: string;

  /** The raw product description */
  descriptionHtml: string;

  /** Product Short Text metafield value {@link productSelect.highlight} */
  productShortText?: string;

  /** The category of product: "Kitchen" | "Laundry" | "Laundry & Household" */
  type: string;

  /** Product selling plans. May be empty as not all our products are subscribeable */
  sellingPlans: DroppsSellingPlan[];
  variants: DroppsVariant[];

  relatedProducts?: string[];
  recommendedProducts?: DroppsProduct[];
  pdpIngredientSection?: PdpIngredientSection | undefined;
  pdpTabData?: PdpTabData | undefined;
  variantMetadata?: Map<number, any>;
  scent?: ScentDetails;
  productGroup?: string;
  productCardDescription?: string;
  recommendedImage?: string | null;
};

export type VideoSource = {
  format: string;
  videoUrl: string;
  mimeType: string;
  height: number;
  width: number;
};

export type MediaImage = {
  url: string;
  alt: string | null;
  width: number;
  height: number;
  altText?: string;
};

export type MediaVideo = {
  url: string;
  alt: string | null;
  width: number;
  height: number;
  sources: VideoSource[];
};

export type ShopifyMediaImage = Extract<ShopifyMedia, { mediaType: "image" }>;

export type ShopifyMedia =
  | (MediaImage & { mediaType: "image" })
  | (MediaVideo & { mediaType: "video" });

// An alias for now, but can be expanded upon when we want to introduce
// additional fields (ie: cadence)
export type DroppsSellingPlan = ShopifySellingPlan;

export type BundledItems = {
  bundledSubscriptionProducts: ShopifyProduct[];
};

export type ProductRating = {
  reviewAverage: number;
  reviewCount: number;
};

export type IngredientSectionDrawerData = {
  ingredientDisclosureLink?: string;
  excludedIngredients?: {
    heading: string;
    ingredients: string[];
  };
  highlightedFeatures?: {
    heading: string;
    features: string[];
  };
  ingredients: {
    heading: string;
    ingredients: {
      name: string;
      type: string;
    }[];
  };
};

export type PdpIngredientSection = {
  ingredientDisclosureUrl?: string;
  ingredientSectionDrawerData?: IngredientSectionDrawerData;
};

export type PdpTabData = {
  overview?: string;
  impact?: string;
  ingredients?: string;
};

// Badge type for product badges - shared across the application
export type Badge = {
  image?: string;
  position?: "top-right" | "bottom-right";
  width?: number;
  text?: string;
};

export type PdpFragranceSectionData = {
  title?: string;
  sectionBankgroundColor?: string;
  scentChipBg?: string;
  benefits?: string[];
  belowBenefitCopy?: string;
};

type MetaobjectFieldGql = {
  key: string;
  value: string;
};

type MetaobjectGql = {
  type: string;
  fields: MetaobjectFieldGql[];
};

export type ScentDetails = {
  title: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  handle: string;
  scentIngredientBackgroundColor?: string;
  ingredients?: ProductIngredients;
  hidden: boolean;
  pdpFragranceSectionContent: PdpFragranceSectionData | null;
  pdpFragranceSectionImage: string | null;
  isFragnanceSectionEnable: boolean;
};

export type UnknownProductIngredients = {
  title: undefined;
  contents: undefined;
  disclosureUrl: undefined;
};

export type KnownProductIngredients = {
  title: string;
  contents: string;
  disclosureUrl?: string;
};

export type ProductIngredients =
  | UnknownProductIngredients
  | KnownProductIngredients;

export type ImageAttributes = {
  src: string;
  alt?: string;
  height?: number;
  width?: number;
  loading?: "eager" | "lazy";
};

export function droppsCatalog(products: ShopifyProduct[]): DroppsCatalog {
  return {
    all: products.map(droppsProduct),
  };
}

/** Return a discount computation when applied at a given price  */
function applySubscriptionDiscount(
  discount: DroppsDiscountFormula,
  price: Money
): DroppsDiscount {
  switch (discount.kind) {
    case "price":
      return discount;
    case "fixed":
      return {
        kind: "fixed",
        offset: discount.offset,
        price: price.sub(discount.offset),
      };
    case "percentage":
      return {
        kind: "percentage",
        percentage: discount.percentage,
        price: price.mul(1 - discount.percentage / 100),
      };
  }
}

/** Multiply a discount by the given factor */
function discountFactor(
  discount: DroppsDiscount,
  factor: number
): DroppsDiscount {
  switch (discount.kind) {
    case "price":
      return {
        kind: "price",
        price: discount.price.mul(factor),
      };
    case "fixed":
      return {
        kind: "fixed",
        offset: discount.offset.mul(factor),
        price: discount.price.mul(factor),
      };
    case "percentage":
      return {
        kind: "percentage",
        percentage: discount.percentage,
        price: discount.price.mul(factor),
      };
  }
}

function parseSize(sizeValue: string): {
  value: string;
  unit: string | undefined;
} {
  //For refills - sizeValue is different. Ex: 6 Refills. Spliting it with space then reverse.
  const sizeArr = sizeValue.includes("Refills")
    ? sizeValue.split(" ").reverse()
    : sizeValue.split("/");
  const size = (
    sizeArr.length > 1 ? parseFloat(sizeArr[1]).toString() : sizeArr[0]
  ).trim();
  const sizeName = sizeArr.length === 1 ? undefined : sizeArr[0].trim();
  return { value: size, unit: sizeName };
}

export function getSizeInfo(options: string[]) {
  if (options.length > 0) {
    return parseSize(options[1] ?? options[0]);
  }
  return { value: "1", unit: "count" };
}

/** Map intermediate variant into dropps variant */
function mapVariant(
  v: ShopifyProductVariant,
  subscription?: DroppsSubscription
): DroppsVariant {
  const size = getSizeInfo(v.options);
  return {
    // kind: "standard",
    id: v.id,
    title: v.title,
    size,
    sku: v.sku ?? undefined,
    inventoryQuantity: v.inventory_quantity,
    requiresShipping: v.requires_shipping,
    backorderEnabled: v.inventory_policy === "continue",
    taxable: v.taxable,
    available: v.available,
    price: v.price,
    weight: v.weight,
    compareAtPrice: v.compare_at_price ?? undefined,
    hidden: v.hidden,
    subscription,
    package_quantity: v.package_quantity,
    default_variant_multiplier: v.variant_multiplier,
    tiered_discount_variant_multiplier: v.tiered_discount_variant_multiplier,
    quantity_discount: v.quantity_discount,
    override_title: v.override_title,
    productSpotDiscount: v.productSpotDiscount,
  };
}

export function droppsProduct({
  variants: rawVariants,
  bundledItems,
  ...product
}: ShopifyProduct): DroppsProduct {
  // All our selling plans SHOULD have the same discount, so take first
  const sellingPlan = product.sellingPlans[0];
  // Directly map over rawVariants without grouping
  const variants = rawVariants.map((variant) => {
    // Check variant-level hide subscription from both direct property (Storefront API path)
    // and variantMetadata (Liquid path where custom metafields are passed separately)
    const variantMeta = product.variantMetadata?.get(variant.id);
    const hideVariantSubscription =
      variant.hide_subscription_button === true ||
      variantMeta?.hide_subscription_button === "true" ||
      variantMeta?.hide_subscription_button === true;

    // PDP Liquid path: `productSpotDiscount` is set as a number on the per-variant
    // metafields bag by `get-formatted-product.liquid`. Merge into variant so
    // pricing helpers (`onetimePrice`, `lowestPrice`, `cartAddLineItem`) see it.
    const spotFromMeta = (() => {
      const raw = variantMeta?.productSpotDiscount;
      const n = parseVariantSpotDiscountDollars(raw);
      return n > 0 ? n : undefined;
    })();
    const variantWithSpot: ShopifyProductVariant = {
      ...variant,
      productSpotDiscount: variant.productSpotDiscount ?? spotFromMeta,
    };

    let subscription: DroppsSubscription | undefined = undefined;
    if (sellingPlan) {
      const option = variant.options[0].toLowerCase();
      if (["wash plan", "subscription"].some((x) => option.includes(x))) {
        subscription = {
          kind: "variant",
          variantId: variant.id,
          hidden:
            variant.hidden ||
            product.hideSubscription ||
            hideVariantSubscription,
          discount: {
            kind: "price",
            price: variant.price,
          },
        };
      } else {
        subscription = {
          kind: "selling-plan",
          hidden: product.hideSubscription || hideVariantSubscription,
          discount: applySubscriptionDiscount(
            sellingPlan.discount,
            variant.price
          ),
        };
      }
    }
    return mapVariant(variantWithSpot, subscription);
  });

  // Sort variants by price
  variants.sort((a, b) => a.price.asCents() - b.price.asCents());

  return {
    ...product,
    bundledItems: bundledItems
      ? bundledItems.bundledSubscriptionProducts.map(droppsProduct)
      : undefined,
    variants,
  };
}

export type DroppsCatalog = {
  all: DroppsProduct[];
};

type OrderableProductOptions = {
  /** Include bulk products as orderable (default: exclude) */
  bulk?: "exclude" | "include";

  /** point of sale for products (default: storefront) */
  pos?: "storefront" | "portal";
};

export const catalogSelect = {
  /** A blank catalog */
  default: () => ({ all: [] }),

  /** A list of products that have available variants for sale */
  orderable: (catalog: DroppsCatalog, options?: OrderableProductOptions) =>
    catalog.all.flatMap((p) => {
      const orderableProduct = productSelect.orderable(p, options);
      return orderableProduct ? [orderableProduct] : [];
    }),
};

export function parseScent(title: string): string | undefined {
  return title
    .split(",")[1]
    ?.replace(/Bulk Pods/g, "")
    ?.trim();
}

export const shortenTitle = (title: string) =>
  title.replace(/laundry & household|pods/gi, "").trim();

export const productSelect = {
  defaultVariant: (p: DroppsProduct) =>
    check(p.variants[0], "missing default variant"),

  selectedVariant: (p: DroppsProduct, id: number) =>
    p.variants.find((v) => v.id === id),

  url: (p: { handle: string }) => `/products/${p.handle}`,

  isBulk: (p: DroppsProduct) => p.title.toLocaleLowerCase().includes("bulk"),

  /** Whether a given product is allowed to be purchased in our subscription portal */
  showInPortal: (p: DroppsProduct) => p.tags.includes("show_in_portal"),

  /**
   * The name of the product is the name shared by all the scents of a product.
   * For the product "Stain & Odor Laundry Detergent Pods, Ocean", returns
   * "Stain & Odor Detergent".
   *
   * The reason why this function isn't restricted to a `DroppsProduct`, so
   * that we can reuse it for use cases like products in cart.
   */
  name: (p: { title: string }) => shortenTitle(p.title.split(",")[0]),

  /**
   * Given the product of "Ultra Concentrated Laundry Detergent Starter Kit
   * Stain & Odor, Lavender Eucalyptus", return "Lavender Eucalyptus". Not all
   * products have scents like dryer balls
   */
  scent: (p: { title: string }) => parseScent(p.title),

  /**
   * Returns the product's featured image. By convention, all orderable
   * products have a featured image enforced.
   */
  featuredImage: (p: DroppsProduct) => {
    const media = check(p.media[0], "no media on product");
    return {
      ...media,
      alt: media.alt ?? undefined,
    };
  },

  /** Returns Product Short Text metafield value */
  highlight: (p: DroppsProduct) =>
    p.productShortText ? p.productShortText : "",

  /** Assuming a product has a selling plan, get the default one */
  defaultSellingPlan: (p: DroppsProduct) => {
    const controlPlan =
      p.sellingPlans.find((s) => s.name === SUBSCRIPTION_CONTROL_PLAN_NAME) ||
      p.sellingPlans[0];

    const controlDiscountPercent =
      controlPlan?.discount.kind === "percentage"
        ? controlPlan.discount.percentage
        : undefined;
    const selectedPlanName = getSubscriptionPlanNameForVariation(
      controlDiscountPercent
    );
    const selectedPlan = p.sellingPlans.find(
      (s) => s.name === selectedPlanName
    );

    // Use variation B/C plan only when that plan exists on the product.
    return selectedPlan || controlPlan;
  },

  /** Find a selling plan that has a given name */
  findSellingPlan: (p: DroppsProduct, plan: DroppsSellingPlan) =>
    p.sellingPlans.find((s) => s.name === plan.name),

  /** Return null if the product is not orderable else included only orderable variants */
  orderable: (
    p: DroppsProduct,
    options?: OrderableProductOptions
  ): DroppsProduct | null => {
    if (p.hidden || p.media.length === 0) {
      return null;
    }

    if (options?.bulk !== "include" && productSelect.isBulk(p)) {
      return null;
    }

    if (options?.pos === "portal" && !productSelect.showInPortal(p)) {
      return null;
    }

    const variants = p.variants
      .map((v) => ({
        ...v,
        available: v.available && (v.backorderEnabled || !!v.inventoryQuantity),
        subscription: variantSelect.orderableSubscription(v),
      }))
      .filter((v) => !v.hidden && v.available);

    if (variants.length === 0) {
      return null;
    }

    return { ...p, variants };
  },
};

const getSubscriptionDiscountsByVariation = (
  product?: DroppsProduct
): Partial<Record<number, number>> | undefined => {
  if (!product) return undefined;
  return getSubscriptionDiscountsByVariationFromPlans(product.sellingPlans, {
    getName: (plan) => plan.name,
    getPercent: (plan) =>
      plan.discount.kind === "percentage"
        ? plan.discount.percentage
        : undefined,
  });
};

/** Applies variant-level `custom.product_spot_discount` (fixed USD off per unit)
 * to a one-time price when GrowthBook `price-discount-test` is variation `1`.
 * Bulk / Bundle products are excluded.
 */
function applyOnetimeSpotToUnitPrice(
  unit: Money,
  variant: { productSpotDiscount?: number } | undefined,
  product: DroppsProduct | undefined,
  useTieredRounding: boolean
): Money {
  if (!product || !variant) return unit;
  const spotDollars = getEffectiveOnetimeSpotDiscountDollars(variant, product, {
    isBundle:
      product.type === "Bundle" ||
      !!product.bundledItems?.length ||
      product.tags?.includes("bundle"),
    isBulk: productSelect.isBulk(product),
  });
  if (spotDollars <= 0) return unit;
  const offset = Money.from(spotDollars, "$");
  const discountedCents = unit.asCents() - offset.asCents();
  if (discountedCents <= 0) return Money.zero;
  return useTieredRounding
    ? Money.fromCentsRoundedUp(discountedCents)
    : Money.from(discountedCents, "¢");
}

export const variantSelect = {
  hasTieredMultiplierFeature: (product?: {
    variants?: Array<{
      tiered_discount_variant_multiplier?: number | null;
      metafields?: any;
    }>;
  }): boolean =>
    !!product?.variants?.some((variant) => {
      const raw =
        variant.tiered_discount_variant_multiplier ??
        variant.metafields?.tiered_discount_variant_multiplier;
      const multiplier = Number(raw);
      return Number.isFinite(multiplier) && multiplier > 1;
    }),

  quantityDiscountPercent: (v: {
    quantity_discount?: number;
    metafields?: any;
  }): number => {
    const raw = Number(
      v.quantity_discount ?? v.metafields?.quantity_discount ?? 0
    );
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  },

  /** Returns one-time price, accounting for tiered multiplier variants */
  onetimePrice: (v: DroppsVariant, product?: DroppsProduct) => {
    if (!product || !variantSelect.hasTieredMultiplierFeature(product)) {
      return applyOnetimeSpotToUnitPrice(v.price, v, product, false);
    }
    const { variantToAdd, quantity } =
      variantSelect.resolveVariantAndQuantityForCartAdd(v, product.variants);
    const qtyDiscountPercent = variantSelect.quantityDiscountPercent(v);
    const discountedUnitPrice =
      qtyDiscountPercent > 0
        ? Money.fromCentsRoundedUp(
            variantToAdd.price.asCents() * (1 - qtyDiscountPercent / 100)
          )
        : variantToAdd.price;
    const withSpot = applyOnetimeSpotToUnitPrice(
      discountedUnitPrice,
      v,
      product,
      true
    );
    return Money.fromCentsRoundedUp(withSpot.asCents() * quantity);
  },

  /**
   * If the subscription is cheaper, returns the subscription price, else
   * returns the onetime price.
   */
  lowestPrice: (v: DroppsVariant, product?: DroppsProduct) => {
    const hasTieredFeature = variantSelect.hasTieredMultiplierFeature(product);
    const { variantToPrice, quantity } =
      hasTieredFeature && product
        ? (() => {
            const resolved = variantSelect.resolveVariantAndQuantityForCartAdd(
              v,
              product.variants
            );
            return {
              variantToPrice: resolved.variantToAdd,
              quantity: resolved.quantity,
            };
          })()
        : { variantToPrice: v, quantity: 1 };

    const sub = variantSelect.orderableSubscription(variantToPrice);
    if (!sub) {
      const qtyDiscountPercent = hasTieredFeature
        ? variantSelect.quantityDiscountPercent(v)
        : 0;
      let discountedUnitPrice =
        qtyDiscountPercent > 0
          ? hasTieredFeature
            ? Money.fromCentsRoundedUp(
                variantToPrice.price.asCents() * (1 - qtyDiscountPercent / 100)
              )
            : variantToPrice.price.mul(1 - qtyDiscountPercent / 100)
          : variantToPrice.price;
      discountedUnitPrice = applyOnetimeSpotToUnitPrice(
        discountedUnitPrice,
        v,
        product,
        hasTieredFeature
      );
      return hasTieredFeature
        ? Money.fromCentsRoundedUp(discountedUnitPrice.asCents() * quantity)
        : discountedUnitPrice.mul(quantity);
    }
    let discount = sub.discount;
    const activeSellingPlan = product
      ? productSelect.defaultSellingPlan(product)
      : undefined;
    if (sub.kind === "selling-plan" && activeSellingPlan) {
      discount = applySubscriptionDiscount(
        activeSellingPlan.discount,
        variantToPrice.price
      );
    }
    const qtyDiscountPercent = hasTieredFeature
      ? variantSelect.quantityDiscountPercent(v)
      : 0;
    // Use global utility for discount override
    if (discount.kind === "percentage") {
      const variationDiscounts = getSubscriptionDiscountsByVariation(product);
      const percent = getEffectiveSubscriptionDiscountPercent(
        discount.percentage,
        {
          isBundle:
            !!product &&
            (product.type === "Bundle" ||
              !!product.bundledItems?.length ||
              product.tags?.includes("bundle")),
          isBulk: !!product && productSelect.isBulk(product),
        },
        variationDiscounts
      );
      // Prefer Shopify-computed per-delivery price to match storefront/cart rounding.
      // Recompute only when A/B test changes the effective percent from control.
      const unitAfterSubDiscount =
        percent === discount.percentage
          ? discount.price
          : variantToPrice.price.mul(1 - percent / 100);
      const unitAfterQtyDiscount =
        qtyDiscountPercent > 0
          ? hasTieredFeature
            ? Money.fromCentsRoundedUp(
                unitAfterSubDiscount.asCents() * (1 - qtyDiscountPercent / 100)
              )
            : unitAfterSubDiscount.mul(1 - qtyDiscountPercent / 100)
          : unitAfterSubDiscount;
      return hasTieredFeature
        ? Money.fromCentsRoundedUp(unitAfterQtyDiscount.asCents() * quantity)
        : unitAfterQtyDiscount.mul(quantity);
    }
    const unitAfterSubDiscount = discount.price ?? variantToPrice.price;
    const unitAfterQtyDiscount =
      qtyDiscountPercent > 0
        ? hasTieredFeature
          ? Money.fromCentsRoundedUp(
              unitAfterSubDiscount.asCents() * (1 - qtyDiscountPercent / 100)
            )
          : unitAfterSubDiscount.mul(1 - qtyDiscountPercent / 100)
        : unitAfterSubDiscount;
    return hasTieredFeature
      ? Money.fromCentsRoundedUp(unitAfterQtyDiscount.asCents() * quantity)
      : unitAfterQtyDiscount.mul(quantity);
  },

  /** Returns if the subscription discount as a percentage */
  subscriptionDiscount: (v: DroppsVariant, product?: DroppsProduct) => {
    const variantToPrice = product
      ? variantSelect.resolveVariantAndQuantityForCartAdd(v, product.variants)
          .variantToAdd
      : v;
    const sub = variantSelect.orderableSubscription(variantToPrice);
    if (!sub) return 0;
    let discount = sub.discount;
    const activeSellingPlan = product
      ? productSelect.defaultSellingPlan(product)
      : undefined;
    if (sub.kind === "selling-plan" && activeSellingPlan) {
      discount = applySubscriptionDiscount(
        activeSellingPlan.discount,
        variantToPrice.price
      );
    }
    // Use global utility for discount override
    if (discount.kind === "percentage") {
      const variationDiscounts = getSubscriptionDiscountsByVariation(product);
      return getEffectiveSubscriptionDiscountPercent(
        discount.percentage,
        {
          isBundle:
            !!product &&
            (product.type === "Bundle" ||
              !!product.bundledItems?.length ||
              product.tags?.includes("bundle")),
          isBulk: !!product && productSelect.isBulk(product),
        },
        variationDiscounts
      );
    }
    return 0;
  },

  /**
   * Percent shown in “Subscribe & Save X%” copy when treatment uses the -15 plan;
   * otherwise same as {@link variantSelect.subscriptionDiscount} (actual plan %).
   */
  subscriptionDiscountForDisplay: (
    v: DroppsVariant,
    product?: DroppsProduct
  ): number => {
    if (
      getSubscriptionDiscountVariation() === 1 &&
      product &&
      productSelect.defaultSellingPlan(product)?.name ===
        SUBSCRIPTION_PLAN_NAME_BY_VARIATION[1]
    ) {
      return SUBSCRIPTION_15_PLAN_DISCOUNT_PERCENT;
    }
    return variantSelect.subscriptionDiscount(v, product);
  },

  /**
   * Returns a formatted string that contains the variant's size value (eg: 64)
   * and unit (eg: "count") for user viewing. Optional quantity formatting.
   */
  sizeFormat: (
    v: { size: DroppsVariant["size"]; override_title?: string },
    qty: number = 1
  ) => {
    if (v.override_title) return v.override_title;
    const val = +v.size.value;
    const fallback = qty != 1 ? `${qty} ${v.size.value}` : v.size.value;
    return `${!isNaN(val) ? val * qty : fallback} ${v.size.unit || ""}`.trim();
  },

  /** artificially differentiate synthetic variants in react lists */
  reactKey: (v: DroppsVariant) => v.id,

  /** Return the subscription variant id (if one exists) */
  subscriptionVariantId: (v: DroppsVariant) =>
    v.subscription?.kind === "variant" ? v.subscription.variantId : v.id,

  /** Returns the orderable subscription */
  orderableSubscription: (v: DroppsVariant) =>
    v.subscription?.hidden ? undefined : v.subscription,

  /**
   * Resolves tiered multiplier variants into the actual line item payload shape.
   * For multiplier variants (>=2), add the base variant (multiplier=1) with
   * quantity equal to the selected multiplier.
   */
  resolveVariantAndQuantityForCartAdd: <
    T extends {
      tiered_discount_variant_multiplier?: number | null;
      metafields?: any;
    }
  >(
    selectedVariant: T,
    variants: T[]
  ): { variantToAdd: T; quantity: number } => {
    const selectedMultiplierRaw =
      selectedVariant.tiered_discount_variant_multiplier ??
      selectedVariant.metafields?.tiered_discount_variant_multiplier;
    const selectedMultiplier = Number(selectedMultiplierRaw);

    if (!Number.isFinite(selectedMultiplier) || selectedMultiplier <= 1) {
      return { variantToAdd: selectedVariant, quantity: 1 };
    }

    const baseVariant = variants.find((variant) => {
      const multiplierRaw =
        variant.tiered_discount_variant_multiplier ??
        variant.metafields?.tiered_discount_variant_multiplier;
      return Number(multiplierRaw) === 1;
    });

    if (!baseVariant) {
      return { variantToAdd: selectedVariant, quantity: 1 };
    }

    return {
      variantToAdd: baseVariant,
      quantity: Math.max(1, Math.round(selectedMultiplier)),
    };
  },

  /** Convert variant into a line item to be added to cart */
  cartAddLineItem: (
    variant: DroppsVariant,
    options: {
      subscription?: DroppsSellingPlan | false;
      quantity?: number;
      /** Used for one-time spot discount line-item property when `price-discount-test` is treatment */
      product?: DroppsProduct;
    }
  ): CartAddLineItem => {
    const quantity = options.quantity ?? 1;
    let variantId = variant.id;
    let sellingPlan: undefined | number = undefined;
    if (options.subscription) {
      sellingPlan = options.subscription.id;
      variantId = variantSelect.subscriptionVariantId(variant);
    }

    const baseProps: Record<string, string | number | boolean> = {
      ...(variant.override_title
        ? { _override_title: variant.override_title }
        : {}),
    };

    const priceTestProps: Record<string, string | number | boolean> = {};
    if (
      getSubscriptionDiscountVariation() === 1 &&
      !options.subscription &&
      options.product
    ) {
      const spotDollars = getEffectiveOnetimeSpotDiscountDollars(
        variant,
        options.product,
        {
          isBundle:
            options.product.type === "Bundle" ||
            !!options.product.bundledItems?.length ||
            options.product.tags?.includes("bundle"),
          isBulk: productSelect.isBulk(options.product),
        }
      );
      if (spotDollars > 0) {
        priceTestProps._ab_price_discount_amount = spotDollars;
      }
    }

    const properties =
      Object.keys({ ...baseProps, ...priceTestProps }).length > 0
        ? { ...baseProps, ...priceTestProps }
        : undefined;

    return {
      id: variantId,
      quantity,
      selling_plan: sellingPlan,
      properties,
    };
  },
};

export type ShopifyProductVariant = {
  available: boolean;
  barcode: string | null;
  compare_at_price: Money | null;
  hidden: boolean;
  id: number;
  inventory_management?: string;
  inventory_policy?: string;
  inventory_quantity?: number;
  options: string[];
  price: Money;
  requires_shipping: boolean;
  sku: string | null;
  taxable?: boolean;
  title: string;
  weight: Mass;
  package_quantity: number;
  variant_multiplier: number;
  tiered_discount_variant_multiplier?: number | null;
  quantity_discount?: number;
  hide_subscription_button?: boolean;
  override_title?: string;
  metafields?: any;
  /** Fixed USD off per unit from variant metafield `custom.product_spot_discount`. */
  productSpotDiscount?: number;
};

export type ShopifyProduct = {
  badge?: Badge;
  collections?: Array<{
    id: string;
    handle: string;
    title: string;
  }>;
  frontBoxImage?: string;
  bundledItems?: BundledItems;
  descriptionHtml: string;
  productShortText: string;
  featuredQuote?: string;
  relatedProducts?: string[];
  recommendedProducts?: DroppsProduct[];
  handle: string;
  hidden: boolean;
  id: number;
  media: ShopifyMedia[];
  options: string[];
  sellingPlans: ShopifySellingPlan[];
  tags: string[];
  title: string;
  type: string;
  variants: ShopifyProductVariant[];
  hideSubscription: boolean;
  overrideFutureShipments?: string;
  pdpIngredientSection?: PdpIngredientSection | undefined;
  pdpTabData?: PdpTabData | undefined;
  variantMetadata?: Map<number, any>;
  scent?: ScentDetails;
  productGroup?: string;
  productCardDescription?: string;
  recommendedImage?: string | null;
};

export type ShopifySellingPlan = {
  id: number;
  name: string;
  discount: DroppsDiscountFormula;
};
